import { formatReferralCode } from "./referralCodes";

const MINI_APP_REF_SEPARATOR = "__";
const START_PARAM_MAX_LENGTH = 64;

function sanitizeBaseSlug(slug: string | null | undefined): string {
  if (typeof slug !== "string") {
    return "";
  }
  const trimmed = slug.trim();
  if (!trimmed) {
    return "";
  }
  const separatorIndex = trimmed.toLowerCase().lastIndexOf(MINI_APP_REF_SEPARATOR);
  if (separatorIndex >= 0) {
    return trimmed.slice(0, separatorIndex);
  }
  return trimmed;
}

function sanitizeReferralForStartParam(referral: string | null | undefined): string | null {
  const formatted = formatReferralCode(referral);
  if (!formatted) {
    return null;
  }
  const safe = formatted.replace(/[^a-z0-9_-]/gi, "");
  if (!safe) {
    return null;
  }
  return safe.toLowerCase();
}

export function buildMiniAppStartParamWithReferral(
  baseSlug: string | null | undefined,
  referral: string | null | undefined,
): string {
  const sanitizedSlug = sanitizeBaseSlug(baseSlug);
  const sanitizedReferral = sanitizeReferralForStartParam(referral);

  if (!sanitizedReferral) {
    return sanitizedSlug.slice(0, START_PARAM_MAX_LENGTH);
  }

  if (!sanitizedSlug) {
    return sanitizedReferral.slice(0, START_PARAM_MAX_LENGTH);
  }

  const combined = `${sanitizedSlug}${MINI_APP_REF_SEPARATOR}${sanitizedReferral}`;
  if (combined.length <= START_PARAM_MAX_LENGTH) {
    return combined;
  }

  return sanitizedSlug.slice(0, START_PARAM_MAX_LENGTH);
}

export function extractReferralFromMiniAppStartParam(
  startParam: string | null | undefined,
): string | null {
  if (typeof startParam !== "string") {
    return null;
  }
  const trimmed = startParam.trim();
  if (!trimmed) {
    return null;
  }
  const separatorIndex = trimmed.indexOf(MINI_APP_REF_SEPARATOR);
  if (separatorIndex < 0) {
    return null;
  }
  const referralCandidate = trimmed.slice(separatorIndex + MINI_APP_REF_SEPARATOR.length);
  if (!referralCandidate) {
    return null;
  }
  const formatted = formatReferralCode(referralCandidate);
  return formatted ?? null;
}

export function buildMiniAppInviteUrl(
  baseUrl: string | null | undefined,
  referral: string | null | undefined,
): string {
  const trimmedBase = typeof baseUrl === "string" ? baseUrl.trim() : "";
  if (!trimmedBase) {
    return "";
  }

  try {
    const parsed = new URL(trimmedBase, trimmedBase.startsWith("http") ? undefined : "https://t.me/");
    const existingStartParam = parsed.searchParams.get("startapp");
    if (!existingStartParam) {
      return parsed.toString();
    }

    const personalisedStartParam = buildMiniAppStartParamWithReferral(existingStartParam, referral);
    if (!personalisedStartParam || personalisedStartParam === existingStartParam) {
      return parsed.toString();
    }

    parsed.searchParams.set("startapp", personalisedStartParam);
    return parsed.toString();
  } catch {
    return trimmedBase;
  }
}
